import { type UserStore } from '@/features/iam/users/UserStore'
import { type WorkspaceMemberStore } from '@/features/iam/members/WorkspaceMemberStore'
import type Session from '@/features/iam/auth/Session'
import { computed, observable } from 'mobx'
import { task } from 'mobx-task'
import { func, required, validationContext } from 'validx'
import { containsOnlyValidPhoneNumberChars } from '@/utils/phone-number-util'
import { type SnackbarErrorHandlerFactory } from '@/features/shell'
import { WorkspaceMember } from '../../members/WorkspaceMember'

/**
 * Manages state related to signing up for a workspace.
 */
export class WorkspaceSignupViewModel {
  @observable accessor firstName = ''
  @observable accessor lastName = ''
  @observable accessor phoneNumber = ''

  /**
   * Validation rules.
   */
  readonly validation = validationContext(this, {
    firstName: [required('First name is a required field.')],
    lastName: [required('Last name is a required field.')],
    phoneNumber: [
      required('Phone number is a required field.'),
      func(
        () =>
          containsOnlyValidPhoneNumberChars(this.phoneNumber) ||
          'The specified phone number is not valid.',
      ),
    ],
  })

  constructor(
    private readonly _session: Session,
    private readonly _userStore: UserStore,
    private readonly _workspaceMemberStore: WorkspaceMemberStore,
    private readonly _createSnackbarErrorHandler: SnackbarErrorHandlerFactory,
    private readonly _onLogOut: () => Promise<unknown>,
  ) {
    // If the user already has these attributes (e.g. social login), set them
    // immediately.
    this.firstName = _session.currentUser.givenName
    this.lastName = _session.currentUser.familyName
    this.phoneNumber = _session.currentUser.phone ?? ''

    this.logout = this.logout.bind(this)
  }

  /**
   * The selected workspace.
   */
  @computed
  get workspace() {
    return this._session.currentWorkspace
  }

  /**
   * Whether external customer is enabled.
   */
  @computed
  get externalCustomerEnabled() {
    return this._session.externalCustomerEnabled
  }

  /**
   * Signs up the user to the workspace.
   */
  completeRegistration = task.resolved(async () => {
    if (!this.validation.reset().validate().isValid) {
      return
    }

    const errorHandler = this._createSnackbarErrorHandler(
      'Unable to complete registration.',
    )

    const member = await this.createOrUpdateMember().catch(errorHandler)

    if (!member) {
      return
    }

    await this._userStore
      .fetchUser(this._session.currentUser.id, true)
      .catch(errorHandler)

    this._session.setMember(member)

    window.dataLayer?.push({
      event: 'registration_complete',
      user_id: this._session.currentUser.id,
      lead_type: this.workspace.classification,
      workspace_id: this.workspace.id,
      email: this._session.currentUser.email,
      phone: this._session.currentUser.phone,
      first_name: this._session.currentUser.givenName,
      last_name: this._session.currentUser.familyName,
    })
  })

  /**
   * Logs out the current user.
   */
  logout() {
    return this._onLogOut()
  }

  /**
   * Creates or updates the member.
   */
  private createOrUpdateMember() {
    return this._session.currentMember === WorkspaceMember.none
      ? this._workspaceMemberStore.signupToWorkspace({
          workspaceId: this.workspace.id,
          internalUserId: this._session.currentUser.internalId,
          firstName: this.firstName,
          lastName: this.lastName,
          phone: this.phoneNumber,
        })
      : this._workspaceMemberStore.updateProfile(this._session.currentMember, {
          familyName: this.lastName,
          givenName: this.firstName,
          phone: this.phoneNumber,
        })
  }
}
